/**
 * Fait : l'écran par lequel le parent administrateur gère sa famille — voir qui
 *   est là, fermer un accès, le rouvrir, inviter quelqu'un, annuler une
 *   invitation. Deux parties séparées : les gens d'un côté (`SectionDeLaFamille`),
 *   les invitations de l'autre (`SectionDesInvitations`). Cette page tient
 *   l'état, appelle l'API, et recharge.
 *
 * Ne fait pas : il n'efface aucun compte — fermer suffit à tout couper, et
 *   effacer poserait la question insoluble des projets dont la personne est
 *   propriétaire — et ne change aucun rôle. Il ne décide d'aucun droit non
 *   plus : ce qu'il montre, c'est ce que l'API a bien voulu rendre. Un membre
 *   ordinaire qui tape cette adresse à la main voit le refus du serveur.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { RoleDeCompte } from '@aymoai/shared';

import { Annonce } from '../gabarits/Annonce.tsx';
import {
  changeLAcces,
  invite,
  listeLaFamille,
  listeLesInvitations,
  revoqueLInvitation,
  type CompteDeLaFamille,
  type InvitationVue,
} from './api.ts';
import { SectionDeLaFamille } from './SectionDeLaFamille.tsx';
import { SectionDesInvitations } from './SectionDesInvitations.tsx';

/** Ce que le serveur a fait du courriel d'invitation — dit sans détour. */
const SORT_DU_COURRIEL: Readonly<Record<string, string>> = {
  envoye: 'Le courriel d’invitation est parti.',
  echec_envoi:
    'L’invitation est créée, mais le courriel n’a pas pu partir. Transmettez le lien vous-même.',
  non_configure:
    'L’invitation est créée. Aucun service de courrier n’est branché : transmettez le lien vous-même.',
};

/**
 * Pourquoi l'écran ne montre rien : l'atelier ne répond pas, ou il a refusé.
 * Les confondre afficherait « cet écran ne vous est pas ouvert » à quelqu'un
 * dont le réseau a simplement coupé — un refus de droit inventé de toutes
 * pièces, que la personne n'a aucun moyen de démentir.
 */
type Empechement = { readonly quoi: 'injoignable' | 'refus'; readonly message: string };

export function PageAdministration() {
  const [comptes, setComptes] = useState<CompteDeLaFamille[] | null>(null);
  const [invitations, setInvitations] = useState<InvitationVue[]>([]);
  const [echec, setEchec] = useState<Empechement | null>(null);

  /** Quand la SECONDE liste manque — la famille, elle, s'affiche quand même. */
  const [echecDesInvitations, setEchecDesInvitations] = useState<string | null>(null);

  const [annonce, setAnnonce] = useState<string | null>(null);

  /** Le compte dont on a cliqué le bouton une première fois. */
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const [adresseInvitee, setAdresseInvitee] = useState('');
  const [roleAttribue, setRoleAttribue] = useState<RoleDeCompte>('member');

  /** Le lien à recopier, montré SEULEMENT quand le courriel n'est pas parti. */
  const [lienARecopier, setLienARecopier] = useState<string | null>(null);

  const titre = useRef<HTMLHeadingElement>(null);
  const dejaAnnonce = useRef(false);

  const recharge = useCallback(async () => {
    const famille = await listeLaFamille();

    if (!famille.bon) {
      setEchec({
        quoi: famille.injoignable === true ? 'injoignable' : 'refus',
        message: famille.erreur.message,
      });
      return;
    }

    setComptes(famille.valeur.comptes);
    setEchec(null);

    // Les invitations sont demandées ENSUITE et leur échec n'efface pas la
    // famille : deux listes, deux appels, et l'un peut manquer sans emporter
    // l'autre. Mais un échec se DIT : la version d'avant rendait une liste
    // vide, et l'écran annonçait « aucune invitation en attente » alors qu'il
    // n'avait simplement pas pu demander.
    const enCoursDInvitation = await listeLesInvitations();

    if (enCoursDInvitation.bon) {
      setInvitations(enCoursDInvitation.valeur);
      setEchecDesInvitations(null);
    } else {
      setInvitations([]);
      setEchecDesInvitations(enCoursDInvitation.erreur.message);
    }
  }, []);

  useEffect(() => {
    void recharge();
  }, [recharge]);

  useEffect(() => {
    // Le focus va au titre à l'ARRIVÉE, une seule fois : sans cela, un lecteur
    // d'écran reste sur le lien de la barre de commandes et ne dit pas où l'on
    // vient d'atterrir. Une seule fois, parce que le refaire à chaque
    // rechargement volerait le focus du bouton qu'on vient d'actionner.
    if (comptes !== null && !dejaAnnonce.current) {
      dejaAnnonce.current = true;
      titre.current?.focus();
    }
  }, [comptes]);

  /** Ferme ou rouvre un accès — le premier clic ne fait qu'armer le second. */
  async function change(compte: CompteDeLaFamille): Promise<void> {
    if (compte.actif && aConfirmer !== compte.id) {
      setAConfirmer(compte.id);
      return;
    }

    setEnCours(true);
    setAConfirmer(null);
    const issue = await changeLAcces(compte.id, !compte.actif);
    setEnCours(false);

    if (!issue.bon) {
      setAnnonce(issue.erreur.message);
      return;
    }

    setAnnonce(
      compte.actif
        ? `L’accès de ${compte.nomAffiche} est fermé.`
        : `L’accès de ${compte.nomAffiche} est rouvert.`,
    );
    await recharge();
  }

  /** Invite une adresse, et dit ce qu'il est advenu du courriel. */
  async function inviteQuelquUn(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setEnCours(true);

    const issue = await invite(adresseInvitee.trim(), roleAttribue);
    setEnCours(false);

    if (!issue.bon) {
      setAnnonce(issue.erreur.message);
      return;
    }

    setAdresseInvitee('');
    setAnnonce(SORT_DU_COURRIEL[issue.valeur.courriel] ?? 'L’invitation est créée.');
    setLienARecopier(
      issue.valeur.courriel === 'envoye'
        ? null
        : `${location.origin}/#/inscription/${issue.valeur.jeton}`,
    );

    await recharge();
  }

  /** Annule une invitation encore vivante. */
  async function annule(invitation: InvitationVue): Promise<void> {
    setEnCours(true);
    const issue = await revoqueLInvitation(invitation.id);
    setEnCours(false);

    // Le lien affiché ne survit pas à l'annulation : le laisser proposerait de
    // transmettre une clé morte.
    if (issue.bon) setLienARecopier(null);

    setAnnonce(
      issue.bon
        ? `L’invitation de ${invitation.adresseInvitee} est annulée.`
        : issue.erreur.message,
    );
    await recharge();
  }

  if (echec !== null) {
    const injoignable = echec.quoi === 'injoignable';

    return (
      <div className="empilement">
        <section data-etat={injoignable ? 'indisponible' : 'erreur'} data-ecran="administration">
          <h2>{injoignable ? 'L’atelier ne répond pas' : 'Cet écran ne vous est pas ouvert'}</h2>
          <Annonce urgence="urgent" nature="refus">
            {echec.message}
          </Annonce>

          {injoignable ? (
            <button type="button" data-role="reessayer" onClick={() => void recharge()}>
              Réessayer
            </button>
          ) : (
            <p>
              <a href="#/">Revenir à vos projets</a>
            </p>
          )}
        </section>
      </div>
    );
  }

  if (comptes === null) {
    return (
      <div className="empilement">
        <section data-etat="chargement" aria-busy="true" data-ecran="administration">
          <h2>Chargement de la famille…</h2>
        </section>
      </div>
    );
  }

  return (
    <div className="empilement" data-etat="nominal" data-ecran="administration">
      {annonce !== null && <Annonce urgence="urgent">{annonce}</Annonce>}

      <SectionDeLaFamille
        comptes={comptes}
        aConfirmer={aConfirmer}
        enCours={enCours}
        refDuTitre={titre}
        surGeste={(compte) => void change(compte)}
      />

      <SectionDesInvitations
        invitations={invitations}
        echec={echecDesInvitations}
        adresseInvitee={adresseInvitee}
        roleAttribue={roleAttribue}
        lienARecopier={lienARecopier}
        enCours={enCours}
        surAdresse={setAdresseInvitee}
        surRole={setRoleAttribue}
        surEnvoi={(evenement) => void inviteQuelquUn(evenement)}
        surAnnulation={(invitation) => void annule(invitation)}
      />
    </div>
  );
}
